// Campaign progress math — single source of truth shared by the selector layer and the progress badge.
//
// The key rule (why this exists): the progress BAR's filled portion and the "X/Y" COUNTER must always
// agree. They used to disagree — the bar filled blue for `sending` while the counter excluded it — so a
// message that reached WhatsApp (`sent_pending_ack`, a real waMessageKey) but never got a delivery ack
// (groups / privacy-restricted recipients) showed a full blue bar over "0/1" forever.
//
// "Processed" = the message left our hands: sent-to-WhatsApp + delivered + read + interacted + failed/expired.
// A `sending` message HAS been sent (that's the campaign's job) — it counts. Only genuinely-queued
// `pending` messages (not yet handed to WhatsApp) are still in progress; they stay the unfilled remainder.

export interface CampaignProgressStats {
    /** Dispatched to WhatsApp (has a real waMessageKey), awaiting a delivery ack. Counts as sent. */
    sending: number;
    /** Still queued locally — not yet handed to WhatsApp. NOT sent; the unfilled remainder of the bar. */
    pending: number;
    delivered: number;
    read: number;
    interacted: number;
    failed: number;
    expired: number;
}

export interface CampaignProgressView {
    total: number;
    /** Numerator of "X/Y" — sent + delivered + read + interacted + failed + expired. */
    totalProcessed: number;
    /** failed + expired (both terminal "done" states). */
    totalFailed: number;
    /** True once every contact has at least been sent → show the Completed badge. */
    isAllProcessed: boolean;
    /** Bar segment widths (%). Their sum equals (totalProcessed / total) * 100 — bar matches the counter. */
    percents: {
        sending: number;
        delivered: number;
        read: number;
        interacted: number;
        failed: number;
        expired: number;
    };
}

export function computeCampaignProgress(
    stats: CampaignProgressStats,
    totalContacts: number,
): CampaignProgressView {
    const total = totalContacts || 0;
    const expiredCount = stats.expired || 0;
    const totalFailed = stats.failed + expiredCount;

    // Sent counts as processed (see file header). `pending` is deliberately excluded so genuinely-queued
    // messages don't inflate the count — they remain the unfilled part of the bar.
    const totalProcessed = stats.sending + stats.delivered + stats.read + stats.interacted + totalFailed;
    const isAllProcessed = total > 0 && totalProcessed >= total;

    const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

    return {
        total,
        totalProcessed,
        totalFailed,
        isAllProcessed,
        percents: {
            sending: pct(stats.sending),
            delivered: pct(stats.delivered),
            read: pct(stats.read),
            interacted: pct(stats.interacted),
            failed: pct(stats.failed),
            expired: pct(expiredCount),
        },
    };
}
